import { globalToastService } from '@repo/atomic-components';
import type { ToolkitTool } from '@/api/toolkit';
import { useEditUserToolkitToolMutation } from '@/api/toolkit';
import { UserRoleType } from '@/api';
import { selectUserAboutData, useAppSelector } from '@/store';
import type { ToolkitPromptFormSubmitHandler } from '@/ui/components/ToolkitPromptFormModal';

export interface UseUpdateUserToolkitPromptOptions {
  tool: ToolkitTool | null;
  onSuccess?: () => void;
  onUpdated?: (tool: ToolkitTool) => void;
}

export interface UseUpdateUserToolkitPromptResult {
  handleSubmit: ToolkitPromptFormSubmitHandler;
  isLoading: boolean;
}

export const useUpdateUserToolkitPrompt = ({
  tool,
  onSuccess,
  onUpdated,
}: UseUpdateUserToolkitPromptOptions): UseUpdateUserToolkitPromptResult => {
  const [editUserToolkitTool, { isLoading }] = useEditUserToolkitToolMutation();
  const userAboutData = useAppSelector(selectUserAboutData);
  const isGlobalAdmin = userAboutData?.role === UserRoleType.GLOBAL_ADMIN;

  const handleSubmit: ToolkitPromptFormSubmitHandler = async (values) => {
    if (!tool) return;

    const toolName = values.toolName.trim();
    const description = values.toolDescription.trim();
    const prompt = values.prompt.trim();

    try {
      const baseToolName = tool.toolName.trim();
      const baseDescription = tool.description.trim();
      const basePrompt = tool.prompt.trim();

      const payload: Parameters<typeof editUserToolkitTool>[0] = {
        toolId: tool.toolId,
      };

      if (isGlobalAdmin) {
        payload.globalAdminAsUser = userAboutData?.userId;
      }

      // Only send fields that actually changed.
      if (toolName && toolName !== baseToolName) payload.toolName = toolName;
      if (description && description !== baseDescription) payload.toolDescription = description;
      if (prompt && prompt !== basePrompt) payload.prompt = prompt;

      const hasChanges = Object.keys(payload).some((k) => k !== 'toolId' && k !== 'globalAdminAsUser');
      if (!hasChanges) {
        globalToastService.show({
          closable: true,
          severity: 'info',
          summary: 'No changes to save',
          life: 6000,
        });
        onSuccess?.();
        return;
      }

      await editUserToolkitTool(payload).unwrap();

      globalToastService.show({
        closable: true,
        severity: 'success',
        summary: 'Your prompt has successfully been edited',
        life: 6000,
      });

      onUpdated?.({
        ...tool,
        toolName: payload.toolName ?? tool.toolName,
        description: payload.toolDescription ?? tool.description,
        prompt: payload.prompt ?? tool.prompt,
      });
      onSuccess?.();
    } catch {
      globalToastService.show({
        closable: true,
        severity: 'error',
        summary: 'Failed to update, please try again.',
        life: 6000,
      });
    }
  };

  return { handleSubmit, isLoading };
};
